import { ArrowLeft, ArrowRight, X, Sparkles, Palette, LayoutGrid, Pencil, Brain, Camera, FileText, Target } from 'lucide-react';
import { useOnboardingStore, UploadedMethodType } from '../../store/onboardingStore';
import { invalidateWebsitesCache } from '../../store/websitesStore';
import { StepProductData } from './steps/StepProductData';
import { StepConfirmScrapedProduct } from './steps/StepConfirmScrapedProduct';
import { ConfirmScrapedProductView } from './ui/ConfirmScrapedProductView';
import { StepAdditionalOffers } from './steps/StepAdditionalOffers';
import { StepBrandAndPrice } from './steps/StepBrandAndPrice';
// StepFunnelCount removed — funnel count is now determined by template selection in StepFunnelConfig
import { StepFunnelConfig } from './steps/StepFunnelConfig';
import { StepFunnelSummary } from './steps/StepFunnelSummary';
import { dictionary, Language } from '../../utils/dictionary';
import { useAuth } from '../../context/AuthContext';
import toast, { Toaster } from 'react-hot-toast';
import { useEffect, useRef, useState } from 'react';
import { axiosInstanceConversationalEngine } from '../../services/instance';
import { MultiFunnelItem, MultiFunnelPayload, MultiFunnelProduct } from '../../services/interface';
import { postBulkFunnels } from '../../services/funnels';
import { createUserBrand } from '../../services/settings/userBrands';
import { creativesService } from '../../feature/ads/cretives/services/creatives.service';
import { userExperimentsService } from '../../services/userExperiments/userExperimentsService';
import { Clock, Users, Shield, Zap, Leaf } from 'lucide-react';
import Loader from '../common/Loader';
import { useTutorialContext } from '../../context/TutorialContext';
import { useNavigate } from 'react-router-dom';
import { Chip } from '../common/chip/Chip';
import { ModalWrapper } from '../modals/ModalWrapper';
import { BuyCreditsModal } from '../modals/BuyCreditsModal';
import { membershipAxios, membershipStatsAxios } from '../../services/membership/MembershipAxios';
import { buildTemplateStudioPages, buildVideoReviewsAddonPages } from '../../templateStudio/onboardingTemplateCompiler';
import { buildOnboardingTemplateDesignPayload, buildUniversalStyleSelectionPayload } from '../../templateStudio/onboardingTemplateSelection.ts';
import { getOnboardingVideoReviewsGifsCredits } from '../../const/onboardingVideoReviews';
import { getSubmittablePromotion } from '../../utils/promotions';
import FluxiLogo from '../../assets/svg/FluxiLogo.svg';
import type { Variant } from '../../types/onboarding';
// import { useTutorialContext } from '../../context/TutorialContext';
// import { TutorialSetup } from '../TutorialSetup';
// import WelcomeModal from '../WelcomeModal';
// import TutorialTooltip from '../TutorialTooltip';


const customToast = (message: string, title: string = 'No pudimos cargar información de la página') => {
  return toast.custom(
    (t) => (
      <div
        className="max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5"
        style={{
          opacity: t.visible ? 1 : 0,
          transform: t.visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'all 0.3s ease-out',
        }}
      >
        <div className="p-4 w-full flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
              <X className="h-5 w-5 text-red-500" />
            </div>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-lg font-medium text-gray-900">
              {title}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {message}
            </p>
          </div>
        </div>
      </div>
    ),
    {
      duration: 4000,
      position: 'top-right',
    }
  );
};

const funnelSubmissionErrorMessage = (error: any) => {
  const candidate =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message;
  return typeof candidate === 'string' && candidate.trim()
    ? candidate.trim()
    : 'Hubo un error creando los embudos. Por favor intenta de nuevo.';
};

const stableStringify = (value: any): string => {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  return `{${Object.keys(value)
    .filter(key => value[key] !== undefined)
    .sort()
    .map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(',')}}`;
};

const hashString = (value: string): string => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const createOnboardingRequestSessionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const buildBulkClientRequestId = (sessionId: string, payload: MultiFunnelPayload) => {
  return `${sessionId}:${hashString(stableStringify(payload))}`;
};

const normalizeFunnelNamePart = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed.replace(/\s+/g, ' ') : '';
};

const MAX_FUNNEL_NAME_WORDS = 5;
const FUNNEL_NAME_TRAILING_JOINERS = new Set([
  'de',
  'del',
  'la',
  'las',
  'el',
  'los',
  'para',
  'por',
  'con',
  'sin',
  'que',
  'y',
  'o',
]);
const FUNNEL_NAME_LOW_SIGNAL_WORDS = new Set([
  ...FUNNEL_NAME_TRAILING_JOINERS,
  'personas',
  'persona',
  'usuarios',
  'usuario',
  'clientes',
  'cliente',
  'compradores',
  'comprador',
  'buscan',
  'busca',
  'un',
  'una',
  'unos',
  'unas',
  'su',
  'sus',
]);

const limitFunnelDisplayName = (value: string) => {
  const words = value
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .slice(0, MAX_FUNNEL_NAME_WORDS);

  while (
    words.length > 1 &&
    FUNNEL_NAME_TRAILING_JOINERS.has(words[words.length - 1].toLowerCase().replace(/[.,:;]+$/g, ''))
  ) {
    words.pop();
  }

  return words.join(' ');
};

const buildFunnelDisplayName = (
  productName: string,
  angleName?: string,
  usedNames?: Set<string>
) => {
  const signalTokens = (value?: string | null) =>
    normalizeFunnelNamePart(value)
      .replace(/[,.;:!¡?¿()[\]{}]+/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 2 && !FUNNEL_NAME_LOW_SIGNAL_WORDS.has(word.toLowerCase()));
  const product = signalTokens(productName).slice(0, 2).join(' ') || normalizeFunnelNamePart(productName) || 'Producto';
  const angle = signalTokens(angleName).slice(0, 3).join(' ') || normalizeFunnelNamePart(angleName) || 'nuevo enfoque';
  const baseName = limitFunnelDisplayName(`${product}, ${angle}`);

  if (!usedNames) return baseName;

  let candidate = baseName;
  let suffix = 2;
  while (usedNames.has(candidate.toLowerCase())) {
    const words = baseName.split(/\s+/).filter(Boolean);
    candidate =
      words.length <= MAX_FUNNEL_NAME_WORDS - 2
        ? `${baseName} / ${suffix}`
        : [...words.slice(0, MAX_FUNNEL_NAME_WORDS - 2), `/ ${suffix}`].join(' ');
    suffix += 1;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
};

type ProductVariantVisualContext = {
  summary: string;
  variations: string[];
  visual_references: Array<{
    variation_name: string;
    image_url: string;
    attribute_name?: string;
    attribute_value?: string;
  }>;
};

const getVariantAttributeValue = (attribute: Variant['attributes'][number]) =>
  String(attribute.value || attribute.color?.name || '').trim();

const buildProductVariantVisualContext = (
  variants: Variant[] | null | undefined
): ProductVariantVisualContext | null => {
  if (!variants?.length) return null;

  const variationNames = Array.from(new Set(
    variants
      .map((variant) => variant.name?.trim())
      .filter((name): name is string => Boolean(name))
  ));
  const references = new Map<string, ProductVariantVisualContext['visual_references'][number]>();

  variants.forEach((variant) => {
    const variantName = variant.name?.trim();
    const variantImage = variant.images?.find(Boolean);
    if (variantName && variantImage) {
      references.set(`${variantName}::${variantImage}`, {
        variation_name: variantName,
        image_url: variantImage,
      });
    }

    variant.attributes?.forEach((attribute) => {
      const attributeValue = getVariantAttributeValue(attribute);
      const imageUrl = attribute.image_url || attribute.imageUrl;
      if (!attributeValue || !imageUrl) return;

      references.set(`${attribute.name}::${attributeValue}::${imageUrl}`, {
        variation_name: attributeValue,
        attribute_name: attribute.name,
        attribute_value: attributeValue,
        image_url: imageUrl,
      });
    });
  });

  if (variationNames.length === 0 && references.size === 0) return null;

  const visibleNames = variationNames.length > 0
    ? variationNames
    : Array.from(new Set(Array.from(references.values()).map((reference) => reference.variation_name)));

  return {
    summary: `Este producto tiene variaciones: ${visibleNames.join(', ')}. Puedes usar una o varias variaciones durante la página cuando aporte visualmente.`,
    variations: visibleNames,
    visual_references: Array.from(references.values()),
  };
};

let activeOnboardingLayouts = 0;
const SALES_ANGLES_TIMEOUT_MS = 420_000;
const SHELL_TRANSITION_MS = 240;
const PANEL_TRANSITION_MS = 280;
const PANEL_OPEN_DELAY_MS = 170;
const SHELL_CLOSE_DELAY_MS = 130;
const FINAL_CREATION_ICONS = [Pencil, Brain, Camera, LayoutGrid, Sparkles, Target, FileText];

export function OnboardingLayout() {
  const { data, setStep, nextStep, previousStep, updateProductData, updateVisualStyle, updateVisualContent, setSalesAngles, updateAdditionalOffers, updateContextData, contextData, hasFetchedSalesAngles, setHasFetchedSalesAngles, resetOnboardingData, setPriceErrors, clearPriceErrors, setShowCustomSectionsToggle, multiFunnelData, isFetchingDesignRecommendation, isFetchingSalesAngleRecommendation, estimatedCost, selectedLegacyTemplates, salesAngles } = useOnboardingStore();
  const { productData } = data;
  const [isLoading, setIsLoading] = useState(false);
  const [funnelSubStep, setFunnelSubStep] = useState(1);
  const { language, country } = useAuth();
  const { step } = data;
  const [productCompleteness, setProductCompleteness] = useState<any>(data.additionalOffers);
  const [showConfirmScrapedProduct, setShowConfirmScrapedProduct] = useState(false);
  const [showDesignAnalysis] = useState(false);
  type ScrapedConfirmItem = 'bump' | { type: 'upsell'; index: number };
  const [scrapedConfirmQueueStep2, setScrapedConfirmQueueStep2] = useState<ScrapedConfirmItem[]>([]);
  const { resumeTutorial, setCurrentStepIndex, isPaused } = useTutorialContext();
  const navigate = useNavigate();
  const [onboardingRequestSessionId] = useState(createOnboardingRequestSessionId);
  const [isOpenBuyCreditsModal, setIsOpenBuyCreditsModal] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  const [endMembershipDate, setEndMembershipDate] = useState("");
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [isFetchingSalesAngles, setIsFetchingSalesAngles] = useState(false);
  const [salesAnglesError, setSalesAnglesError] = useState<string | null>(null);
  const [shellVisible, setShellVisible] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [isShellClosing, setIsShellClosing] = useState(false);
  const salesAnglesRequestRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    const timers: number[] = [];
    const frame = window.requestAnimationFrame(() => {
      setShellVisible(true);
      timers.push(window.setTimeout(() => setPanelVisible(true), PANEL_OPEN_DELAY_MS));
    });

    return () => {
      window.cancelAnimationFrame(frame);
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  useEffect(() => {
    activeOnboardingLayouts += 1;

    return () => {
      activeOnboardingLayouts -= 1;
      window.setTimeout(() => {
        if (activeOnboardingLayouts === 0) {
          useOnboardingStore.getState().resetOnboardingData();
        }
      }, 0);
    };
  }, []);

  useEffect(() => {
    setLoadingCredits(true);
    membershipStatsAxios().then((res) => {
      if (res?.active_membership) {
        setUserCredits(res.active_membership.credits);
        setEndMembershipDate(res.active_membership.ends_at);
      }
      setLoadingCredits(false);
    }).catch(() => setLoadingCredits(false));
  }, []);

  // Design analysis screen removed — funnels are now initialized inside StepFunnelConfig

  // Design recommendation is fetched when moving from step 3 → 4 (product confirmed).
  // NOT on mount — product data isn't ready yet at mount time.

  useEffect(() => {
    if (step === 1 && isPaused) {
      setCurrentStepIndex(1);
      resumeTutorial();
    }
  }, [step]);

  useEffect(() => {
    setProductCompleteness(data.additionalOffers);
  }, [data.additionalOffers]);

  useEffect(() => {
    const hasPrincipalProduct =
      Boolean(data.productData.name?.trim()) &&
      Boolean(data.productData.details?.trim()) &&
      ((data.visualContent.images?.length ?? 0) > 0 || (data.visualContent.imagesBase64?.length ?? 0) > 0);

    if (step > 1 && !hasPrincipalProduct) {
      setStep(1);
      customToast(
        dictionary["product-information-incomplete"]?.[language],
        dictionary["product-information-incomplete-title"]?.[language]
      );
    }
  }, [
    step,
    data.productData.name,
    data.productData.details,
    data.visualContent.images?.length,
    data.visualContent.imagesBase64?.length,
    language,
    setStep,
  ]);

  useEffect(() => {
    if (step !== 2) setScrapedConfirmQueueStep2([]);
  }, [step]);

  useEffect(() => {
    const fetchExperiments = async () => {
      try {
        const response = await userExperimentsService.getExperiments();
        const websiteToggle = response.experiments?.website_creation_toggle;
        if (websiteToggle?.enabled === "true") {
          setShowCustomSectionsToggle(true);
        }
      } catch (error) {
        console.error("Error fetching user experiments:", error);
      }
    };
    fetchExperiments();
  }, []);

  // const {
  //   isWelcomeOpen,
  //   isTutorialActive,
  //   currentStep: tutorialStep,
  //   startTutorial,
  //   nextStep: nextTutorialStep,
  //   closeTutorial
  // } = useTutorialContext();

  const currentConfirmStep2 = scrapedConfirmQueueStep2[0];

  const renderStepContent = () => {
    // Show AI design analysis screen between step 4b → 5b while recommendation loads
    if (showDesignAnalysis) {
      return <DesignAnalysisScreen language={language} />;
    }

    if (step === 1 && showConfirmScrapedProduct) {
      return (
        <StepConfirmScrapedProduct
          onBack={() => {
            // Reset everything so user starts fresh with a new product
            setShowConfirmScrapedProduct(false);
            updateProductData({
              ...data.productData,
              productLink: '',
              name: '',
              details: '',
              price: '',
              externalId: null,
              providerId: null,
              variants: null,
              isManualEntry: false,
            });
            updateVisualContent({ images: [], imagesBase64: [] });
            updateAdditionalOffers({
              ...data.additionalOffers,
              bumpOffer: undefined,
              upsell: { ...data.additionalOffers.upsell, enabled: false, products: [] },
            });
          }}
          onConfirm={() => {
            setShowConfirmScrapedProduct(false);
            fetchSalesAngles();
            nextStep();
          }}
        />
      );
    }

    if (step === 2 && currentConfirmStep2) {
      const bumpOffer = data.additionalOffers.bumpOffer;
      const upsellProducts = data.additionalOffers.upsell?.products ?? [];

      if (currentConfirmStep2 === 'bump' && bumpOffer) {
        const bumpImages = bumpOffer.url_images ? [bumpOffer.url_images] : [];
        return (
          <ConfirmScrapedProductView
            title={dictionary['confirm-order-bump']?.[language] ?? 'Confirma tu Order Bump'}
            subtitle={dictionary['edit-data-if-needed']?.[language]}
            name={bumpOffer.productName || ''}
            description={bumpOffer.description || ''}
            images={bumpImages}
            onNameChange={(value) =>
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: { ...bumpOffer, productName: value },
              })
            }
            onDescriptionChange={(value) =>
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: { ...bumpOffer, description: value },
              })
            }
            onImagesUpdate={(images) =>
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: { ...bumpOffer, url_images: images[0] ?? '' },
              })
            }
            onBack={() => {
              setScrapedConfirmQueueStep2([]);
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: {
                  ...bumpOffer,
                  productName: '',
                  description: '',
                  url_images: '',
                  image: '',
                  externalId: null,
                  providerId: null,
                  isManualEntry: false,
                  uploadedMethod: UploadedMethodType.LINK,
                },
                upsell: {
                  ...data.additionalOffers.upsell!,
                  products: upsellProducts.map((p) => ({
                    ...p,
                    productName: '',
                    description: '',
                    url_images: [],
                    image: undefined,
                    externalId: null,
                    providerId: null,
                    isManualEntry: false,
                    uploadedMethod: UploadedMethodType.LINK,
                  })),
                },
              });
            }}
            onConfirm={() => {
              const nextQueue = scrapedConfirmQueueStep2.slice(1);
              setScrapedConfirmQueueStep2(nextQueue);
              if (nextQueue.length === 0) nextStep();
            }}
            maxImages={1}
          />
        );
      }

      if (currentConfirmStep2 !== 'bump' && upsellProducts[currentConfirmStep2.index]) {
        const upsellIndex = currentConfirmStep2.index;
        const product = upsellProducts[upsellIndex];
        const productImages = product.url_images ?? [];
        const imagesArray = Array.isArray(productImages) ? productImages : [productImages].filter(Boolean);
        return (
          <ConfirmScrapedProductView
            title={dictionary['confirm-upsell']?.[language] ?? 'Confirma tu Upsell'}
            subtitle={dictionary['edit-data-if-needed']?.[language]}
            name={product.productName || ''}
            description={product.description || ''}
            images={imagesArray}
            onNameChange={(value) => {
              const next = [...upsellProducts];
              next[upsellIndex] = { ...product, productName: value };
              updateAdditionalOffers({
                ...data.additionalOffers,
                upsell: { ...data.additionalOffers.upsell!, products: next },
              });
            }}
            onDescriptionChange={(value) => {
              const next = [...upsellProducts];
              next[upsellIndex] = { ...product, description: value };
              updateAdditionalOffers({
                ...data.additionalOffers,
                upsell: { ...data.additionalOffers.upsell!, products: next },
              });
            }}
            onImagesUpdate={(images) => {
              const next = [...upsellProducts];
              next[upsellIndex] = { ...product, url_images: images };
              updateAdditionalOffers({
                ...data.additionalOffers,
                upsell: { ...data.additionalOffers.upsell!, products: next },
              });
            }}
            onBack={() => {
              setScrapedConfirmQueueStep2([]);
              updateAdditionalOffers({
                ...data.additionalOffers,
                bumpOffer: data.additionalOffers.bumpOffer
                  ? {
                      ...data.additionalOffers.bumpOffer,
                      productName: '',
                      description: '',
                      url_images: '',
                      image: '',
                      externalId: null,
                      providerId: null,
                      isManualEntry: false,
                      uploadedMethod: UploadedMethodType.LINK,
                    }
                  : undefined,
                upsell: {
                  ...data.additionalOffers.upsell!,
                  products: upsellProducts.map((p) => ({
                    ...p,
                    productName: '',
                    description: '',
                    url_images: [],
                    image: undefined,
                    externalId: null,
                    providerId: null,
                    isManualEntry: false,
                    uploadedMethod: UploadedMethodType.LINK,
                  })),
                },
              });
            }}
            onConfirm={() => {
              const nextQueue = scrapedConfirmQueueStep2.slice(1);
              setScrapedConfirmQueueStep2(nextQueue);
              if (nextQueue.length === 0) nextStep();
            }}
          />
        );
      }
    }

    switch (step) {
      case 1:
        return <StepProductData isScraping={isLoading} />;
      case 2:
        return <StepAdditionalOffers isScraping={isLoading} />;
      case 3:
        return <StepBrandAndPrice />;
      case 4:
        return (
          <StepFunnelConfig
            onComplete={handleContinue}
            onBack={previousStep}
            onSubStepChange={setFunnelSubStep}
            isFetchingSalesAngles={isFetchingSalesAngles}
            salesAnglesError={salesAnglesError}
            onRetrySalesAngles={() => fetchSalesAngles()}
          />
        );
      case 5:
        return <StepFunnelSummary />;
      default:
        return <StepProductData />;
    }
  };

  const handleProductScrape = async () => {
    if (!data.productData.productLink) {
      return true;
    }

    setIsLoading(true);
    let success = false;

    try {
      const response = await axiosInstanceConversationalEngine.post('/scrape-product', {
        product_url: data.productData.productLink,
        country: country
      });

      const productData = response.data.data;

      const hasName = productData.name && productData.name !== "unknown";
      const hasDescription = productData.description && productData.description !== "unknown";
      const hasImages = productData.images && productData.images.length > 0;

      if (!hasName && !hasDescription && !hasImages) {
        customToast(dictionary["product-not-found"][language], dictionary["product-not-found-title"][language]);
        return false;
      }

      let variants = null;
      if (productData.provider_id === 'dropi') {
        variants = productData.variants || null;
      }

      updateProductData({
        ...data.productData,
        name: hasName ? productData.name : '',
        price: '',  // user must enter price — scraped price is supplier's, not selling price
        details: hasDescription ? productData.description : '',
        isManualEntry: false,
        externalId: productData.external_id,
        providerId: productData.provider_id,
        // Producto nuevo => se re-deriva el tipo (Dropi -> dropshipping) al montar el paso 3
        inventoryType: undefined,
        paymentMethod: 'cod',
        variants: variants
      });

      if (productData.images && productData.images.length > 0) {
        updateVisualContent({
          images: productData.images
        });
      }

      success = true;
    } catch (err) {
      console.error('Error fetching product:', err);
      customToast(dictionary["failed-to-fetch-product-data"][language], dictionary["failed-to-fetch-product-data-title"][language]);
      success = false;
    } finally {
      setIsLoading(false);
    }

    return success;
  };

  const maxStep = 5;
  const isLastStep = step === maxStep;

  const fallbackVideoReviewsGifsCredits = getOnboardingVideoReviewsGifsCredits(multiFunnelData.funnels);
  const requiredCredits = estimatedCost?.total_cost ?? ((multiFunnelData.funnels.length * 85) + fallbackVideoReviewsGifsCredits);

  const validateCredits = async (): Promise<boolean> => {
    let latestCredits = userCredits;
    try {
      const membership = await membershipAxios();
      latestCredits = membership?.credits ?? userCredits;
      setUserCredits(latestCredits);
    } catch (error) {
      console.error('Error refreshing credits before funnel generation:', error);
    }

    if (latestCredits < requiredCredits) {
      setIsOpenBuyCreditsModal(true);
      return false;
    }
    return true;
  };

  const handleCreditsRefreshed = (newCredits: number) => {
    setUserCredits(newCredits);
  };

  const handleShellBack = () => {
    if (isShellClosing) return;

    setIsShellClosing(true);
    setPanelVisible(false);

    window.setTimeout(() => setShellVisible(false), SHELL_CLOSE_DELAY_MS);
    window.setTimeout(() => {
      if (window.history.length > 1) {
        navigate(-1);
        return;
      }
      navigate('/funnels');
    }, SHELL_CLOSE_DELAY_MS + Math.max(SHELL_TRANSITION_MS, PANEL_TRANSITION_MS));
  };

  const handleProductCompleteness = (data: any) => {
    updateAdditionalOffers(data);
    setProductCompleteness(data);
  }

  interface FetchSalesAnglesProps {
    productName?: string;
    productDescription?: string;
  }

  const fetchSalesAngles = async ({ productName = '', productDescription = '' }: FetchSalesAnglesProps = {}) => {
    const currentState = useOnboardingStore.getState();
    if (currentState.salesAngles.length > 0 && currentState.hasFetchedSalesAngles) {
      setIsLoading(false);
      setSalesAnglesError(null);
      return;
    }

    if (salesAnglesRequestRef.current) {
      return salesAnglesRequestRef.current;
    }

    const requestedProductName = currentState.data.productData?.name || productData?.name || productName;
    const requestedProductDescription = currentState.data.productData?.details || productData?.details || productDescription;

    const request = (async () => {
      setIsFetchingSalesAngles(true);
      setSalesAnglesError(null);
      setHasFetchedSalesAngles(false);

      try {
        const response = await axiosInstanceConversationalEngine.post('/resolve-info-funnel', {
          product_name: requestedProductName,
          product_description: requestedProductDescription,
          language: language,
        }, { timeout: SALES_ANGLES_TIMEOUT_MS });

        const currentProduct = useOnboardingStore.getState().data.productData;
        if (
          (currentProduct.name || '') !== (requestedProductName || '') ||
          (currentProduct.details || '') !== (requestedProductDescription || '')
        ) {
          return;
        }

        if (!response.data || !Array.isArray(response.data.sales_angles) || response.data.sales_angles.length === 0) {
          throw new Error('No sales angles returned by resolve-info-funnel');
        }

        const apiSalesAngles = response.data.sales_angles.map((angle: any, index: number) => {
          const iconMap = [Clock, Users, Shield, Zap, Leaf];
          const id = angle.name ? angle.name.toLowerCase().replace(/\s+/g, '-') : `angle-${index}`;
          const complementaryAngles = Array.isArray(angle.complementary_angles)
            ? angle.complementary_angles.filter((item: unknown) => typeof item === 'string' && item.trim().length > 0)
            : [];

          return {
            id,
            title: angle.name || `Angle ${index + 1}`,
            description: angle.description || 'No description available',
            complementary_angles: complementaryAngles,
            icon: iconMap[index % iconMap.length],
            isRecommended: index === 0
          };
        });

        updateContextData({
          pain_detection: response.data.pain_detection || '',
          buyer_detection: response.data.buyer_detection || '',
          recommended_sales_angles: response.data.sales_angles || [],
        });

        setSalesAngles(apiSalesAngles);
        setHasFetchedSalesAngles(true);

        // Fire AI angle selection — raw angles from API have { name, description }
        // LEGACY DISABLED: sales_angle_selector made an extra LangChain call
        // after sales_angles_v2. The new /resolve-info-funnel returns angles
        // already ordered by priority, so the first one is the default.
      } catch (error) {
        console.error("Error fetching sales angles:", error);
        const currentProduct = useOnboardingStore.getState().data.productData;
        const status = (error as any)?.response?.status;
        const isGatewayTimeout = status === 504;
        if (
          (currentProduct.name || '') !== (requestedProductName || '') ||
          (currentProduct.details || '') !== (requestedProductDescription || '')
        ) {
          return;
        }
        setSalesAngles([]);
        updateContextData({
          pain_detection: 'FUNNEL_RESEARCH_FAILED',
          buyer_detection: 'No pudimos realizar la investigación del producto.',
          recommended_sales_angles: [],
        });
        const errorMessage = isGatewayTimeout
          ? (dictionary["funnel-research-error-description"]?.[language] ??
              'El análisis del producto tomó más de lo esperado. Inténtalo nuevamente en unos minutos.')
          : (dictionary["sales-angles-error-message"]?.[language] ??
              "No pudimos generar ángulos de venta de calidad. Intenta de nuevo.");
        setSalesAnglesError(errorMessage);
        customToast(
          errorMessage,
          dictionary["funnel-research-error-title"]?.[language] ?? 'Investigación no disponible'
        );
        setHasFetchedSalesAngles(true);
      } finally {
        setIsLoading(false);
        setIsFetchingSalesAngles(false);
        salesAnglesRequestRef.current = null;
      }
    })();

    salesAnglesRequestRef.current = request;
    return request;
  };

  const handleContinue = async () => {
    if (step === 1) {
      if (data.productData.productLink &&
        (!data.productData.name || !data.productData.details || !data.visualContent.images?.length)) {
        const scrapingSuccessful = await handleProductScrape();

        if (!scrapingSuccessful) return;
        setShowConfirmScrapedProduct(true);
        return;
      }

      if (!data.productData.name || !data.productData.details || (!data.visualContent.images?.length && !data.visualContent.imagesBase64?.length)) {
        customToast(dictionary["product-information-incomplete"][language], dictionary["product-information-incomplete-title"][language]);
        return;
      }

      fetchSalesAngles();
      nextStep();
    } else if (step === 2) {
      const shouldScrapeBumpOffer = data.additionalOffers.bumpOffer?.enabled && data.additionalOffers.bumpOffer?.productLink && data.additionalOffers.bumpOffer?.uploadedMethod === UploadedMethodType.LINK;

      const upsellEnabled = data.additionalOffers.upsell?.enabled &&
        data.additionalOffers.upsell.products?.some(p =>
          p.productLink || (p.productName && p.price && p.description && p.image)
        );


      setIsLoading(true);

      try {
        const promises = [];

        if (shouldScrapeBumpOffer &&
          (!data.additionalOffers.bumpOffer?.isManualEntry ||
            (!data.additionalOffers.bumpOffer.productName &&
              !data.additionalOffers.bumpOffer.price &&
              !data.additionalOffers.bumpOffer.description))) {

          const bumpOfferPromise = axiosInstanceConversationalEngine.post('/scrape-product', {
            product_url: data.additionalOffers.bumpOffer?.productLink || ''
          }).then(response => {
            return { type: 'bumpOffer', data: response.data.data };
          });

          promises.push(bumpOfferPromise);
        }

        if (upsellEnabled) {
          const upsellPromises = data.additionalOffers.upsell.products
            .filter(product => product.productLink && !product.isManualEntry)
            .map((product, index) => {
              return axiosInstanceConversationalEngine.post('/scrape-product', {
                product_url: product.productLink
              }).then(response => {
                return {
                  type: 'upsell',
                  index: index,
                  originalProduct: product,
                  data: response.data.data
                };
              });
            });

          promises.push(...upsellPromises);
        }

        const results = await Promise.all(promises);

        const updatedAdditionalOffersNew = { ...data.additionalOffers };
        const updatedUpsellProducts = [...data.additionalOffers.upsell.products];

        results.forEach(result => {
          if (result.type === 'bumpOffer' && data.additionalOffers.bumpOffer) {
            const productData = result.data;
            updatedAdditionalOffersNew.bumpOffer = {
              ...data.additionalOffers.bumpOffer,
              productName: productData.name || '',
              price: productData.external_sell_price ? productData.external_sell_price.toString() : '0',
              description: productData.description && productData.description !== 'unknown' ? productData.description : '',
              url_images: productData.images && productData.images.length > 0 ? productData.images[0] : '',
              image: '',
              externalId: productData.external_id || null,
              providerId: productData.provider_id || null,
              isManualEntry: false
            };
          } else if (result.type === 'upsell') {
            const productData = result.data;
            updatedUpsellProducts[(result as any).index] = {
              ...(result as any).originalProduct,
              productName: productData.name,
              price: productData.external_sell_price.toString(),
              description: productData.description && productData.description !== 'unknown' ? productData.description : '',
              image: '',
              url_images: productData.images?.[0] ? [productData.images[0]] : [],
              externalId: productData.external_id || null,
              providerId: productData.provider_id || null,
              isManualEntry: false
            };
          }
        });

        if (upsellEnabled) {
          updatedAdditionalOffersNew.upsell = {
            ...data.additionalOffers.upsell,
            products: updatedUpsellProducts
          };
        }

        handleProductCompleteness(updatedAdditionalOffersNew);

        const confirmQueue: ScrapedConfirmItem[] = [];
        results.forEach((result: any) => {
          if (result.type === 'bumpOffer') confirmQueue.push('bump');
          if (result.type === 'upsell') confirmQueue.push({ type: 'upsell', index: result.index });
        });

        if (confirmQueue.length > 0) {
          setScrapedConfirmQueueStep2(confirmQueue);
          setIsLoading(false);
          return;
        }

        if (updatedAdditionalOffersNew.bumpOffer?.enabled) {
          const bumpOffer = updatedAdditionalOffersNew.bumpOffer;
          if (!bumpOffer.productName || !bumpOffer.description || (!bumpOffer.image && !bumpOffer.url_images)) {
            customToast(dictionary["order-bump-information-incomplete"][language]);
            setIsLoading(false);
            return;
          }
        }

        if (updatedAdditionalOffersNew.upsell?.enabled && updatedAdditionalOffersNew.upsell.products?.length > 0) {
          for (const product of updatedAdditionalOffersNew.upsell.products) {
            if (!product.productName || !product.price || !product.description || (!product.image && !((product.url_images ?? []).length > 0))) {
              customToast(dictionary["upsell-information-incomplete"][language]);
              setIsLoading(false);
              return;
            }
          }
        }

        nextStep();
      } catch (err) {
        console.error('Error fetching product data:', err);
        customToast(dictionary["failed-to-fetch-product-data"][language], dictionary["failed-to-fetch-product-data-title"][language]);
      } finally {
        setIsLoading(false);
      }
    } else if (step === 3) {
      const newPriceErrors: { main?: string; bump?: string; upsell?: string } = {};

      if (!data.productData.price || !data.productData.price.trim() || parseFloat(data.productData.price) <= 0) {
        newPriceErrors.main = dictionary["price-required"][language];
      }
      if (data.additionalOffers.bumpOffer?.enabled) {
        if (!data.additionalOffers.bumpOffer?.price || !data.additionalOffers.bumpOffer?.price.trim() || parseFloat(data.additionalOffers.bumpOffer?.price) <= 0) {
          newPriceErrors.bump = dictionary["price-required"][language];
        }
      }
      if (data.additionalOffers.upsell?.enabled) {
        if (data.additionalOffers.upsell?.products?.some(product => !product.price || !product.price.trim() || parseFloat(product.price) <= 0)) {
          newPriceErrors.upsell = dictionary["price-required"][language];
        }
      }

      if (Object.keys(newPriceErrors).length > 0) {
        setPriceErrors(newPriceErrors);
        return;
      }

      clearPriceErrors();
      nextStep();
    } else if (step === 4) {
      // StepFunnelConfig calls onComplete → go to summary
      nextStep();
    } else {
      nextStep();
    }
  };

  // ─── Build shared products array ────────────────────────
  const buildProducts = (): MultiFunnelProduct[] | null => {
    if (parseFloat(data.productData.price) === 0) {
      customToast('The main product price cannot be 0.', 'Main Product Price Incomplete');
      return null;
    }

    const products: MultiFunnelProduct[] = [
      {
        ...(data.productData.uploadedMethod === UploadedMethodType.PREVIOUS && data.productData.externalId
          ? { product_id: data.productData.externalId }
          : {}),
        external_id: data.productData.externalId || null,
        provider_id: data.productData.providerId || null,
        name: data.productData.name,
        description: data.productData.details,
        price: parseFloat(data.productData.price),
        offer_price: parseFloat(data.productData.price),
        country_code: data.productData.countryCode,
        currency_code: data.productData.currencyCode,
        offer_country_code: data.productData.countryCode,
        offer_currency_code: data.productData.currencyCode,
        url_images: data.visualContent.images || [],
        images: data.visualContent.imagesBase64 || [],
        type: 'PRINCIPAL',
        // No forzar 'own': se envía lo elegido (el paso 3 obliga a elegir). Si por algo
        // llega vacío, el backend lo deja "sin definir" en vez de clasificarlo mal.
        inventory_type: data.productData.inventoryType || undefined,
        payment_method: data.productData.paymentMethod || undefined,
        variants: data.productData.variants || null,
      },
    ];

    if (productCompleteness?.upsell?.enabled && productCompleteness.upsell.products?.length > 0) {
      for (const product of productCompleteness.upsell.products) {
        if (product.productName && product.price) {
          products.push({
            ...(product.uploadedMethod === UploadedMethodType.PREVIOUS && product.externalId
              ? { product_id: product.externalId }
              : {}),
            external_id: product.externalId || null,
            provider_id: product.providerId || null,
            name: product.productName,
            description: product.description || '',
            price: parseFloat(product.price) || 0,
            offer_price: parseFloat(product.price) || 0,
            country_code: data.productData.countryCode,
            currency_code: data.productData.currencyCode,
            offer_country_code: data.productData.countryCode,
            offer_currency_code: data.productData.currencyCode,
            url_images: product.url_images || [],
            type: 'UP_SELL',
          });
        }
      }
    }

    if (productCompleteness?.bumpOffer?.enabled && productCompleteness.bumpOffer.productName && productCompleteness.bumpOffer.price) {
      products.push({
        ...(productCompleteness.bumpOffer.uploadedMethod === UploadedMethodType.PREVIOUS && productCompleteness.bumpOffer.externalId
          ? { product_id: productCompleteness.bumpOffer.externalId }
          : {}),
        external_id: productCompleteness.bumpOffer.externalId || null,
        provider_id: productCompleteness.bumpOffer.providerId || null,
        name: productCompleteness.bumpOffer.productName,
        description: productCompleteness.bumpOffer.description || '',
        price: parseFloat(productCompleteness.bumpOffer.price) || 0,
        offer_price: parseFloat(productCompleteness.bumpOffer.price) || 0,
        country_code: data.productData.countryCode,
        currency_code: data.productData.currencyCode,
        offer_country_code: data.productData.countryCode,
        offer_currency_code: data.productData.currencyCode,
        url_images: productCompleteness.bumpOffer.url_images ? [productCompleteness.bumpOffer.url_images] : [],
        type: 'BUMP_OFFER',
      });
    }

    return products;
  };

  // ─── Unified submit: fires bulk + individual legacy in parallel ─────
  const handleSubmitMixed = async () => {
    if (!(await validateCredits())) return;

    setIsLoading(true);
    try {
      const products = buildProducts();
      if (!products) { setIsLoading(false); return; }

      const warrantyDays = data.productData.warranty === 'none' ? 0 :
        data.productData.warranty === 'custom' ?
          parseInt(data.productData.customWarrantyDays) : 30;

      let brandName = data.visualStyle.brandName || '';
      let resolvedBrandId = data.visualStyle.brandId || null;
      if (!resolvedBrandId && data.visualStyle.logoImage) {
        const createdBrand = await createUserBrand({
          name: brandName.trim() || data.productData.name || 'Marca',
          logo_image: data.visualStyle.logoImage,
          brand_colors: {},
          auto_generate_metadata: true,
        });
        resolvedBrandId = createdBrand.id;
        brandName = createdBrand.name;
        updateVisualStyle({
          brandId: createdBrand.id,
          brandContext: createdBrand,
          brandName: createdBrand.name,
          logoImage: undefined,
        });
      }

      const allFunnels = multiFunnelData.funnels;
      const legacyCount = selectedLegacyTemplates.length;
      const newCount = allFunnels.length - legacyCount;

      // Split funnels: first newCount are AI templates, rest are legacy
      const newFunnels = allFunnels.slice(0, newCount);
      const legacyFunnels = allFunnels.slice(newCount);

      // ── Build unified funnel array (AI + legacy in one bulk call) ────────────
      const submittedBrandName = brandName.trim() || data.productData.name || 'Marca';
      const unifiedFunnels: MultiFunnelItem[] = [];
      const usedFunnelNames = new Set<string>();

      // AI template funnels
      newFunnels.forEach(funnel => {
        const nameFunnel = buildFunnelDisplayName(
          data.productData.name,
          funnel.selectedAngles[0]?.name,
          usedFunnelNames
        );
        const selectedTemplateDesign = buildOnboardingTemplateDesignPayload(funnel);
        const universalStyleSelection = buildUniversalStyleSelectionPayload(funnel);
        if (
          funnel.universalStyleEngineVersion === 'universal_slots_v1' &&
          !universalStyleSelection
        ) {
          throw new Error(
            'La composición universal seleccionada está incompleta. Vuelve a elegir el estilo antes de crear el embudo.'
          );
        }

        unifiedFunnels.push({
          brand_name: submittedBrandName,
          name_funnel: nameFunnel,
          ...(resolvedBrandId ? { brand_id: resolvedBrandId } : {}),
          sale_angles: funnel.selectedAngles,
          free_shipping: data.productData.freeShipping,
          country_code: funnel.countryCode,
          currency_code: funnel.currencyCode,
          warranties: warrantyDays,
          promotion: getSubmittablePromotion(funnel.promotion, funnel.designTemplateFamilyMetadata),
          ...(selectedTemplateDesign ? { template_design: selectedTemplateDesign } : {}),
          ...(universalStyleSelection
            ? { universal_style_selection: universalStyleSelection }
            : {
                template_structure: {
                  template_pages: buildTemplateStudioPages(funnel),
                },
                is_custom_sections_only: true,
              }),
        });
      });

      // Legacy template funnels
      legacyFunnels.forEach((funnel, idx) => {
        const legacyTemplate = selectedLegacyTemplates[idx];
        const saleAngles = funnel?.selectedAngles?.length > 0
          ? funnel.selectedAngles
          : data.visualContent.selectedAngle;
        const nameFunnel = buildFunnelDisplayName(
          data.productData.name,
          saleAngles[0]?.name,
          usedFunnelNames
        );

        unifiedFunnels.push({
          brand_name: submittedBrandName,
          name_funnel: nameFunnel,
          ...(resolvedBrandId ? { brand_id: resolvedBrandId } : {}),
          sale_angles: saleAngles,
          free_shipping: data.productData.freeShipping,
          country_code: funnel.countryCode,
          currency_code: funnel.currencyCode,
          warranties: warrantyDays,
          promotion: getSubmittablePromotion(funnel.promotion),
          template_design: {
            theme: legacyTemplate.styleSheet,
            primary_color: data.visualStyle.colorPalette || '#FFFFFF',
            secondary_color: '#FFFFFF',
            template_name: legacyTemplate.name,
            cover_style: legacyTemplate.coverStyle,
            style_sheet: legacyTemplate.styleSheet,
            palette: legacyTemplate.palette,
            template_family: 'sophisticated-html-v2',
          },
          ...(funnel.includeVideoReviewsGifs
            ? {
                template_structure: {
                  template_pages: buildVideoReviewsAddonPages(legacyTemplate.id || legacyTemplate.name || 'legacy-template'),
                },
              }
            : {}),
          is_custom_sections_only: false,
        });
      });

      // Single bulk call for ALL funnels (1 product, 1 base image, sequential processing)
      // Fix #6 (2026-05-19): forward additional_user_instructions (typed in StepFunnelSummary)
      // so the ecommerce dispatcher can propagate it to every section's
      // parameters["additional_instructions"] and CE prompts include it as user_instructions.
      const additionalUserInstructions = (data.additionalUserInstructions ?? '').trim();
      const productVariantVisualContext = buildProductVariantVisualContext(data.productData.variants);
      const bulkPayload: MultiFunnelPayload = {
        products,
        funnels: unifiedFunnels,
        data_for_context: {
          pain_detection: contextData.pain_detection,
          buyer_detection: contextData.buyer_detection,
          recommended_sales_angles: contextData.recommended_sales_angles,
          ...(additionalUserInstructions
            ? { additional_user_instructions: additionalUserInstructions }
            : {}),
          ...(productVariantVisualContext
            ? { product_variant_visual_context: productVariantVisualContext }
            : {}),
        },
      };

      const bulkResponse = await postBulkFunnels({
        ...bulkPayload,
        client_request_id: buildBulkClientRequestId(onboardingRequestSessionId, bulkPayload),
      });

      // Fire async creative generation for AI funnels that have ad templates
      const { adTemplates, sectionTemplates } = useOnboardingStore.getState();
      for (let i = 0; i < newFunnels.length; i++) {
        const funnel = newFunnels[i];
        const selectedTemplateIds = funnel.selectedAdTemplates || [];
        if (selectedTemplateIds.length === 0) continue;

        const websiteId = bulkResponse[i]?.id;
        if (!websiteId) continue;

        const matchingSectionTemplate = sectionTemplates.find(
          t => t.type === funnel.designTemplateType && t.section_type === 'preview'
        );
        const primaryColor = (matchingSectionTemplate?.metadata as any)?.primary_color ?? null;
        const backgroundColor = (matchingSectionTemplate?.metadata as any)?.background_color ?? null;

        const selectedAdStyles = selectedTemplateIds
          .map(id => adTemplates.find(t => t.id === id))
          .filter((t): t is NonNullable<typeof t> => !!t);

        const templates = selectedAdStyles.map(template => ({
          user_prompt: '',
          image_template: template.image_url || template.coverImage,
          format: '1:1' as const,
          template_id: template.name_id || '',
          color_mode: 'custom' as const,
          primary_color: primaryColor,
          background_color: backgroundColor,
          reference_type: 'template' as const,
          config: { language },
          ...(funnel.selectedAngles.length > 0
            ? { angles: funnel.selectedAngles[0].name }
            : {}),
        }));

        creativesService.generateCreativesAsync(websiteId, {
          product_name: data.productData.name,
          product_image: data.visualContent.images?.[0] || '',
          product_description: data.productData.details,
          templates,
        }).catch(err => {
          console.error(`Error generating creatives for funnel ${funnel.name}:`, err);
        });
      }

      // Navigate to funnels on success
      if (bulkResponse.length > 0) {
        invalidateWebsitesCache();
        resetOnboardingData();
        navigate('/funnels');
      } else {
        customToast('No se pudo crear ningún embudo. Por favor intenta de nuevo.', 'Error de creación');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error submitting funnels:', error);
      customToast(funnelSubmissionErrorMessage(error), 'Error de creación');
      setIsLoading(false);
    }
  };

  const hasTextValue = (value?: string | null) => Boolean(value?.trim());
  const hasMainProductImage = () => (data.visualContent.images?.length ?? 0) > 0 || (data.visualContent.imagesBase64?.length ?? 0) > 0;
  const hasResolvedMainProduct = () => hasTextValue(data.productData.name) && hasTextValue(data.productData.details) && hasMainProductImage();
  const canShowStepOneContinue = () => {
    if (data.productData.uploadedMethod === UploadedMethodType.LINK) {
      return hasTextValue(data.productData.productLink) || hasResolvedMainProduct();
    }

    if (data.productData.uploadedMethod === UploadedMethodType.MANUAL) {
      return hasResolvedMainProduct();
    }

    if (data.productData.uploadedMethod === UploadedMethodType.PREVIOUS) {
      return hasTextValue(data.productData.externalId) || hasResolvedMainProduct();
    }

    return hasResolvedMainProduct();
  };
  const hasOfferImage = (image?: string, urlImages?: string | string[]) => {
    if (hasTextValue(image)) return true;
    if (Array.isArray(urlImages)) return urlImages.some((url) => hasTextValue(url));
    return hasTextValue(urlImages);
  };
  const canContinueOffer = (offer: {
    enabled?: boolean;
    uploadedMethod?: UploadedMethodType;
    productLink?: string | null;
    productName?: string;
    description?: string;
    image?: string;
    url_images?: string | string[];
  }) => {
    if (!offer.enabled) return true;
    const hasResolvedProduct = hasTextValue(offer.productName) &&
      hasTextValue(offer.description) &&
      hasOfferImage(offer.image, offer.url_images);

    if (offer.uploadedMethod === UploadedMethodType.LINK) {
      return hasTextValue(offer.productLink) || hasResolvedProduct;
    }

    return hasResolvedProduct;
  };
  const canContinueAdditionalOffers = () => {
    const bumpOfferOk = canContinueOffer(data.additionalOffers.bumpOffer ?? { enabled: false });
    const upsell = data.additionalOffers.upsell;
    const upsellsOk = !upsell?.enabled || (
      upsell.products.length > 0 &&
      upsell.products.every((product) => canContinueOffer({ ...product, enabled: true }))
    );
    return bumpOfferOk && upsellsOk;
  };

  const stepIsDisabled = (step === 1 && !canShowStepOneContinue()) || (step === 2 && !canContinueAdditionalOffers()) || (step === 3 && (data.visualStyle.brandName === '' || data.productData.inventoryType == null));
  const isWaitingForAnglesOnAngleStep = step === 4 && funnelSubStep === 3 && isFetchingSalesAngles;
  const isButtonDisabled = stepIsDisabled || isLoading || isWaitingForAnglesOnAngleStep || isFetchingSalesAngleRecommendation || isFetchingDesignRecommendation;
  const isAngleLoadingStep = step === 4 && funnelSubStep === 3 && (isFetchingSalesAngles || isFetchingSalesAngleRecommendation || (!hasFetchedSalesAngles && salesAngles.length === 0));
  const isCompactAngleStep = step === 4 && funnelSubStep === 3 && (isAngleLoadingStep || multiFunnelData.funnels.length <= 2);
  const showFooterNavigation = !showDesignAnalysis && !(step === 1 && showConfirmScrapedProduct) && !(step === 2 && scrapedConfirmQueueStep2.length > 0) && step !== 4;
  const hasActivePromotions = multiFunnelData.funnels.some(funnel => funnel.promotion?.enabled);
  const showSubmitLoadingOverlay = isLastStep && isLoading;
  const isExpandedProductStep = step === 1 && (showConfirmScrapedProduct || data.productData.isManualEntry);
  const stepFourMaxWidthClass = step === 4 && funnelSubStep === 3
    ? (hasActivePromotions ? 'max-w-[64rem]' : 'max-w-3xl')
    : 'max-w-5xl';
  const stepFourCardClass = isCompactAngleStep
    ? 'rounded-[40px] max-h-[calc(100vh-100px)] overflow-hidden flex flex-col'
    : 'rounded-[40px] h-[calc(100vh-100px)] max-h-[calc(100vh-100px)] overflow-hidden flex flex-col';
  const standardCardFrameClass = 'rounded-[40px] max-h-[calc(100vh-100px)] overflow-hidden flex flex-col';
  const panelMaxWidthClass = showSubmitLoadingOverlay
    ? 'max-w-2xl'
    : step === 4
      ? stepFourMaxWidthClass
      : isExpandedProductStep
        ? 'max-w-4xl'
        : 'max-w-2xl';
  const cardFrameClass = step === 4
    ? stepFourCardClass
    : isExpandedProductStep
      ? 'rounded-[40px] h-[calc(100vh-100px)] overflow-hidden flex flex-col'
      : standardCardFrameClass;
  const cardShellClass = showSubmitLoadingOverlay
    ? 'bg-transparent shadow-none overflow-visible'
    : `bg-white shadow-xl ${cardFrameClass}`;
  const cardContentClass = showSubmitLoadingOverlay
    ? 'p-0'
    : step === 4 || isExpandedProductStep
      ? 'flex min-h-0 flex-1 flex-col p-6 md:p-8'
      : 'flex min-h-0 flex-1 flex-col p-8 md:p-12';
  const stepContentClass = showSubmitLoadingOverlay
    ? ''
    : step === 4
      ? 'min-h-0 flex-1'
      : 'min-h-0 flex-1 overflow-y-auto pr-1';
  const shouldShowProductTopBar = step > 1 && hasResolvedMainProduct();
  const productTopBarImage = data.visualContent.imagesBase64?.[0] || data.visualContent.images?.[0] || '';
  const productTopBarName = data.productData.name?.trim() || 'Selecciona un producto';

  return (
    <>
      {/* <TutorialSetup />
      <WelcomeModal
        isOpen={isWelcomeOpen}
        onStart={startTutorial}
        onClose={closeTutorial}
      />
      <TutorialTooltip
        step={tutorialStep}
        onNext={nextTutorialStep}
        onClose={closeTutorial}
        isActive={isTutorialActive}
      /> */}
      <Toaster position="top-right" />
      {isOpenBuyCreditsModal && (
        <ModalWrapper isOpen={isOpenBuyCreditsModal} onClose={() => setIsOpenBuyCreditsModal(false)}>
          <BuyCreditsModal
            onClose={() => setIsOpenBuyCreditsModal(false)}
            nextRenewalDate={endMembershipDate}
            language={language}
            openInNewTab={true}
            onCreditsRefreshed={handleCreditsRefreshed}
          />
        </ModalWrapper>
      )}
      <div className="fixed inset-0 z-40 flex items-center justify-center overflow-hidden p-4">
        <div
          className={`absolute inset-0 bg-white/90 backdrop-blur-md transition-opacity duration-150 ease-out ${
            shellVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDuration: `${SHELL_TRANSITION_MS}ms` }}
        />
        {/* Custom sections toggle removed — all users now go through the unified flow */}
        <div
          className={`fixed left-6 top-6 z-30 flex items-center gap-3 transition-[opacity,transform] duration-150 ease-out md:left-8 md:top-7 ${
            shellVisible ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'
          }`}
          style={{ transitionDuration: `${SHELL_TRANSITION_MS}ms` }}
        >
          <button
            type="button"
            onClick={handleShellBack}
            aria-label="Volver"
            className="flex h-8 w-8 items-center justify-center text-slate-700 transition hover:text-fluxi"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
          </button>
          <img src={FluxiLogo} alt="Fluxi" className="h-7 w-auto -translate-y-0.5" />
        </div>

        <div
          className={`fixed top-[22px] right-[24px] z-30 transition-[opacity,transform] duration-150 ease-out ${
            shellVisible ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'
          }`}
          style={{ transitionDuration: `${SHELL_TRANSITION_MS}ms` }}
        >
          <Chip
            text={dictionary["credits"][language]}
            value={userCredits.toString()}
            onClick={() => setIsOpenBuyCreditsModal(true)}
            loading={loadingCredits}
          />
        </div>

        <div
          className={`relative z-10 w-full transition-[opacity,transform] duration-200 ease-out ${
            panelVisible ? 'scale-100 opacity-100' : 'scale-[0.985] opacity-0'
          }`}
          style={{ transitionDuration: `${PANEL_TRANSITION_MS}ms` }}
        >
          <div className={`mx-auto w-full ${panelMaxWidthClass} transition-all duration-300`}>
            <div className={cardShellClass}>
              {!showSubmitLoadingOverlay && shouldShowProductTopBar && (
                <PageProductTopBar
                  imageUrl={productTopBarImage}
                  name={productTopBarName}
                />
              )}
              <div className={cardContentClass}>
                <div className={stepContentClass}>
                  {showSubmitLoadingOverlay ? <FinalCreationLoadingScreen language={language} /> : renderStepContent()}
                </div>

                {!showSubmitLoadingOverlay && showFooterNavigation && (
                  <div className={`${step === 1 ? 'flex justify-end items-end' : 'flex justify-between items-center'} mt-8 shrink-0`}>
                    {step > 1 && (
                      <button
                        onClick={() => {
                          if (step === 2) {
                            // Going back to step 1: if product was scraped, show confirm screen
                            // so user can edit before going further back
                            if (data.productData.name && data.visualContent.images?.length) {
                              setShowConfirmScrapedProduct(true);
                              previousStep();
                              return;
                            }
                          }
                          previousStep();
                        }}
                        className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
                      >
                        <ArrowLeft className="w-5 h-5" />
                        {dictionary["back"][language]}
                      </button>
                    )}

                    <div className="flex items-center justify-end gap-4">
                      {isLastStep && (
                        <div className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#F1F5F9] rounded-full text-xs text-[#64748B]">
                          <span className="font-semibold text-gray-600">{dictionary["cost"][language]}:</span>
                          <span className="font-bold text-gray-700">
                            {requiredCredits} {dictionary["credits"][language]}
                          </span>
                        </div>
                      )}

                      <button
                        disabled={isButtonDisabled}
                        onClick={isLastStep ? handleSubmitMixed : handleContinue}
                        className={`${isButtonDisabled ? 'bg-[#E2E8F0]' : isLastStep ? 'group relative overflow-hidden bg-gradient-to-r from-fluxi via-[#2F6EDA] to-[#1D4ED8] hover:from-[#2F6EDA] hover:to-[#2558B8] shadow-lg shadow-blue-200' : 'bg-fluxi hover:bg-fluxi-dark'} ml-auto flex items-center gap-2 px-7 py-2.5 text-sm text-white rounded-full font-medium transition-all`}
                      >
                        {isLastStep && !isButtonDisabled && (
                          <span className="pointer-events-none absolute inset-y-0 -left-8 w-12 rotate-12 bg-white/25 blur-md transition-transform duration-700 group-hover:translate-x-40" />
                        )}
                        {isLoading ? <Loader size={20} color="#FFFFFF" /> : null}
                        {isLastStep
                          ? (dictionary["generate"]?.[language] ?? "Generar")
                          : dictionary["continue"][language]
                        }
                        {!isLastStep && <ArrowRight className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function PageProductTopBar({
  imageUrl,
  name,
}: {
  imageUrl?: string;
  name: string;
}) {
  return (
    <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-slate-200/70 bg-slate-50 px-6 py-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <div className="h-9 w-9 flex-shrink-0 overflow-hidden rounded-md bg-white ring-1 ring-slate-200">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={name}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-slate-100" />
          )}
        </div>
        <div className="min-w-0">
          <p className="max-w-[260px] truncate text-base font-semibold leading-tight text-foreground">
            {name}
          </p>
        </div>
      </div>
    </div>
  );
}

function FinalCreationLoadingScreen({ language }: { language: Language }) {
  const [activeIconIndex, setActiveIconIndex] = useState(0);
  const ActiveIcon = FINAL_CREATION_ICONS[activeIconIndex];

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveIconIndex((current) => (current + 1) % FINAL_CREATION_ICONS.length);
    }, 1400);
    return () => window.clearInterval(interval);
  }, []);

  return (
    <div className="flex min-h-[520px] flex-col items-center justify-center text-center">
      <div className="relative mb-8">
        <div className="flex h-36 w-36 items-center justify-center rounded-full bg-[#2F6EDA] shadow-[0_28px_70px_rgba(47,110,218,0.30)]">
          <ActiveIcon
            key={activeIconIndex}
            className="h-14 w-14 animate-[pulse_1.4s_ease-in-out_infinite] text-white transition-all duration-500"
            strokeWidth={2.4}
          />
        </div>
        <div className="absolute inset-0 rounded-full bg-[#2F6EDA]/20 animate-ping" />
        <div className="absolute inset-4 rounded-full border border-white/45" />
        <div className="absolute -bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-white px-2 py-1 shadow-sm ring-1 ring-slate-200">
          {FINAL_CREATION_ICONS.map((Icon, index) => (
            <Icon
              key={index}
              className={`h-3.5 w-3.5 transition-colors duration-300 ${
                index === activeIconIndex ? 'text-[#2F6EDA]' : 'text-slate-300'
              }`}
              strokeWidth={2.4}
            />
          ))}
        </div>
      </div>
      <h2 className="text-2xl md:text-3xl font-black text-gray-900">
        {language === 'en' ? 'Creating your funnels' : language === 'pt' ? 'Criando seus funis' : 'Creando tus embudos'}
      </h2>
    </div>
  );
}

// ─── Design Analysis Loading Screen ──────────────────────────
function DesignAnalysisScreen({ language }: { language: Language }) {
  const [activeStep, setActiveStep] = useState(0);

  const analysisSteps = [
    {
      icon: Sparkles,
      label: dictionary["analyzing-product"]?.[language] ?? "Analizando tu producto...",
    },
    {
      icon: Palette,
      label: dictionary["selecting-design"]?.[language] ?? "Seleccionando el mejor diseño...",
    },
    {
      icon: LayoutGrid,
      label: dictionary["optimizing-sections"]?.[language] ?? "Optimizando las secciones...",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % analysisSteps.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      {/* Animated AI icon */}
      <div className="relative mb-8">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-fluxi to-fluxi flex items-center justify-center animate-pulse">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
        <div className="absolute inset-0 w-20 h-20 rounded-full bg-gradient-to-br from-fluxi to-fluxi opacity-20 animate-ping" />
      </div>

      {/* Title */}
      <h2 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-2">
        {dictionary["fluxi-analyzing-title"]?.[language] ?? "Fluxi está analizando tu producto"}
      </h2>
      <p className="text-sm text-[#64748B] text-center mb-10 max-w-md">
        {dictionary["fluxi-analyzing-subtitle"]?.[language] ?? "Estamos encontrando el diseño y las secciones más efectivas para tu tipo de producto"}
      </p>

      {/* Animated steps */}
      <div className="w-full max-w-sm space-y-3">
        {analysisSteps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === activeStep;
          const isDone = index < activeStep;

          return (
            <div
              key={index}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-500 ${
                isActive
                  ? 'bg-[#F5F8FF] border border-fluxi/20 shadow-sm'
                  : isDone
                    ? 'bg-[#F0FDF4] border border-green-200'
                    : 'bg-[#F8FAFC] border border-transparent'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                  isActive
                    ? 'bg-fluxi text-white'
                    : isDone
                      ? 'bg-green-500 text-white'
                      : 'bg-[#E2E8F0] text-gray-400'
                }`}
              >
                {isDone ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <span
                className={`text-sm font-medium transition-all duration-500 ${
                  isActive ? 'text-fluxi' : isDone ? 'text-green-600' : 'text-gray-400'
                }`}
              >
                {step.label}
              </span>
              {isActive && (
                <div className="ml-auto">
                  <Loader size={16} color="#4368E5" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
